import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronRight, Home } from 'lucide-react';

/**
 * Breadcrumb Component - v3.5 Structured Data
 * 
 * Generates:
 * 1. Visual breadcrumb trail
 * 2. JSON-LD BreadcrumbList schema for SEO
 */
export const Breadcrumb = ({ items = [] }) => {
    if (!items || items.length === 0) return null;

    // Build JSON-LD schema
    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url ? `https://xtreamgate.com${item.url}` : undefined
        }))
    };

    return (
        <>
            {/* JSON-LD Schema */}
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            </Helmet>

            {/* Visual Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
                <ol className="flex items-center flex-wrap gap-1 text-sm text-muted-foreground">
                    <li className="flex items-center">
                        <Link
                            to="/"
                            className="hover:text-white transition-colors flex items-center gap-1"
                        >
                            <Home className="w-3.5 h-3.5" />
                            <span className="sr-only">Home</span>
                        </Link>
                    </li>
                    {items.map((item, index) => (
                        <li key={index} className="flex items-center">
                            <ChevronRight className="w-3.5 h-3.5 mx-1 text-white/30" />
                            {item.url && index < items.length - 1 ? (
                                <Link
                                    to={item.url}
                                    className="hover:text-white transition-colors"
                                >
                                    {item.name}
                                </Link>
                            ) : (
                                <span className="text-white/70 font-medium truncate max-w-[200px]">
                                    {item.name}
                                </span>
                            )}
                        </li>
                    ))}
                </ol>
            </nav>
        </>
    );
};

/**
 * Helper: Generate breadcrumb items from article
 */
export const generateBreadcrumbs = (article) => {
    if (!article) return [];

    const crumbs = [
        { name: 'Blog', url: '/blog' }
    ];

    // Add cluster/category
    if (article.category) {
        const categorySlug = article.category.toLowerCase().replace(/\s+/g, '-');
        crumbs.push({
            name: article.category,
            url: `/clusters/${categorySlug}`
        });
    }

    // Add article title (no URL - current page)
    crumbs.push({
        name: article.title,
        url: null
    });

    return crumbs;
};

export default Breadcrumb;
